import useSWRInfinite from 'swr/infinite';

// Types
import type { AlbumType } from '@/lib/albums/types';
import type { SocialProfileType } from '@/types/socialProfileType';
import type { AxiosError } from 'axios';
import type { SWRInfiniteConfiguration } from 'swr/infinite';

// Routes
import { ALBUMS } from '@/routes/apiEndpoints';

// Utils
import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

export type AlbumsDataType = {
    data: AlbumType[];
    cursor: string;
};

type UseAlbumsProps = {
    activeSPID: SocialProfileType['id'];
    limit: number;
    params?: {
        name?: string;
    };
    options: SWRInfiniteConfiguration<AlbumsDataType, AxiosError<Error>>;
    cancelToken?: AbortController;
};

const getKey = (
    pageIndex: number,
    previousPageData: AlbumsDataType,
    activeSPID: SocialProfileType['id'],
    limit: number,
    params: UseAlbumsProps['params']
) => {
    // Reached the end
    if (previousPageData && !previousPageData.data) return null;

    // First page, we don't have `previousPageData`
    const url = `${injectEndpointUrlParams(ALBUMS, {
        SPID: activeSPID
    })}?per_page=${limit}`;
    if (pageIndex === 0) return [url, JSON.stringify(params)];

    // Add the cursor to the API endpoint
    return [`${url}&cursor=${previousPageData.cursor}`, JSON.stringify(params)];
};

const useAlbums = ({
    activeSPID,
    limit = 20,
    params = {},
    options = {},
    cancelToken
}: UseAlbumsProps) => {
    const {
        data: albums,
        size,
        setSize,
        isLoading,
        mutate: mutateAlbums,
        error: albumsError
    } = useSWRInfinite<AlbumsDataType, AxiosError<Error>>(
        (pageIndex, previousPageData) =>
            getKey(pageIndex, previousPageData, activeSPID, limit, params),
        ([url]: string[]) =>
            get(url, params, { signal: cancelToken?.signal }).then(
                (res: { data: AlbumsDataType }) => res.data
            ),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    const isLoadingMore = size > 0 && albums && typeof albums[size - 1] === 'undefined';

    return {
        albums,
        mutateAlbums,
        albumsError,
        loadMoreAlbums: () => setSize(size + 1), // Function to load more albums (load more button)
        isLoadingInitialData: isLoading, // Check if it's the initial loading (not the load more)
        isLoadMore: albums && typeof albums[albums.length - 1]?.cursor === 'string', // Check if there is a cursor to active the load more button
        isLoadingMore // Check if the load more is loading
    };
};
export default useAlbums;
